import React from 'react';

import { creativesServiceConfigured } from '../creative-api';
import type { TenantSourceResult } from '../creatives-tenant-source';
import {
  dropPageSections,
  getPageSectionsShared,
  publishPageArrangement,
  savePageArrangementDraft,
  type PageArrangement,
  type PageSectionRef,
  type PageSectionsState,
} from '../page-arrangement-api';

/** Where a load of one page's arrangement has got to. `unavailable` is a route not deployed here (404);
 *  `failed` is any other GET error; `idle` is the disabled state (no page source or no service). */
export type PageArrangementStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'failed';

export interface UsePageArrangementInput {
  /** The page's section source (the `dynamic-page-element` one); absent disables the hook. */
  sourceId?: string | null;
  /** The brand's casino instance the arrangement is keyed by; absent disables the hook. */
  casinoInstanceId?: string;
  /** The brand's site, named to the service on every read and write: needed for a page under one of its list pages
   *  that the platform has not made yet (see `SitePage.pageSourceId`). Absent names none. */
  domain?: string;
  /** How long after the last edit the draft PUT fires (default 700ms). */
  debounceMs?: number;
}

export interface PageArrangementSource {
  status: PageArrangementStatus;
  /** The platform's sections now, in its order. */
  platform: PageSectionRef[];
  /** The brand's working arrangement, with any edit made this session. Empty when there is none. */
  draft: PageArrangement;
  /** What the site serves. Empty when the brand never published this page. */
  published: PageArrangement;
  /** Keep a new working arrangement now; PUT it after the debounce, under the brand and page it was made on. */
  setDraft: (arrangement: PageArrangement) => void;
  /** Put `arrangement` on the site; cancels the pending draft PUT (the publish carries it). */
  publish: (arrangement: PageArrangement) => Promise<TenantSourceResult<PageSectionsState>>;
  /** Read the page's state from the service again, never from the brand's shared read (it is dropped first). */
  reload: () => void;
}

const EMPTY: PageArrangement = { order: [] };

type Held = { platform: PageSectionRef[]; draft: PageArrangement; published: PageArrangement };
const NOTHING_HELD: Held = { platform: [], draft: EMPTY, published: EMPTY };

type PendingDraft = { casinoInstanceId: string; sourceId: string; domain: string; arrangement: PageArrangement };

/**
 * Owns load / debounced draft autosave / publish for ONE page's arrangement, the way `useOrderedSource`
 * does for one ordered list: a switch of page or brand flushes the previous key's pending draft under the
 * brand it was made on, ignores the previous read's answer, and re-reads; an async result applies only while
 * it is still the current key. The page is read through the brand's shared read (`getPageSectionsShared`):
 * it opens at once on a page another reader (the Website hub's Game Lists card) read within the minute, and
 * leaving it never cancels a read another reader waits on. `reload` drops that read first, so it asks the
 * service.
 */
export const usePageArrangement = (input: UsePageArrangementInput): PageArrangementSource => {
  const { sourceId, casinoInstanceId, domain = '', debounceMs = 700 } = input;
  const enabled = Boolean(sourceId && casinoInstanceId) && creativesServiceConfigured();
  const key = `${sourceId ?? ''}|${casinoInstanceId ?? ''}|${domain}`;

  const [held, setHeld] = React.useState<Held>(NOTHING_HELD);
  const [status, setStatus] = React.useState<PageArrangementStatus>('idle');
  const [reloadTick, setReloadTick] = React.useState(0);
  // The key the state above belongs to. A switch of page or brand drops the previous page's state WHILE RENDERING, as
  // the site bridge drops its page's (see `useSiteBridge`), not only in the effect below: that runs after the commit,
  // and the commit that switched drew the new page on the page left (`ready`, with its rows), which Game Lists took for
  // the new page's arrangement and drew its chips on.
  const [stateKey, setStateKey] = React.useState(key);
  if (stateKey !== key) {
    setStateKey(key);
    setHeld(NOTHING_HELD);
    setStatus(enabled ? 'loading' : 'idle');
  }

  // Starts on no key, so the first load counts as a switch and reports `loading`.
  const keyRef = React.useRef('');
  const pendingRef = React.useRef<PendingDraft | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // The last draft write sent, settled or not. A read waits for it: sent together, the read could answer
  // first with the draft stored BEFORE the write, and put that older draft back on screen. That is what a
  // reload does right after an edit (a delete reloads the page), so an edit made just before came undone.
  const writingRef = React.useRef<Promise<unknown>>(Promise.resolve());
  // Counts the owner's edits, and holds the last. A read or a publish that was sent before an edit answers with
  // the draft from before it: it must not put that back over the edit.
  const draftSeqRef = React.useRef(0);
  const lastDraftRef = React.useRef<PageArrangement | null>(null);

  const send = React.useCallback((write: PendingDraft): void => {
    writingRef.current = savePageArrangementDraft(
      write.casinoInstanceId,
      write.sourceId,
      write.arrangement,
      write.domain,
    );
  }, []);

  const clearTimer = (): void => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  };

  // Sends the pending draft now, under the key it was queued on, whatever the current key is.
  const flushDraft = React.useCallback((): void => {
    clearTimer();
    const write = pendingRef.current;
    if (!write) return;
    pendingRef.current = null;
    send(write);
  }, [send]);

  const setDraft = React.useCallback(
    (arrangement: PageArrangement): void => {
      // Guarded here, not only by a disabled control: no page or no service, no write.
      if (!casinoInstanceId || !sourceId || !creativesServiceConfigured()) return;
      draftSeqRef.current += 1;
      lastDraftRef.current = arrangement;
      setHeld((prev) => ({ ...prev, draft: arrangement }));
      const write: PendingDraft = { casinoInstanceId, sourceId, domain, arrangement };
      clearTimer();
      pendingRef.current = write;
      timerRef.current = setTimeout(() => {
        timerRef.current = undefined;
        if (pendingRef.current !== write) return;
        pendingRef.current = null;
        send(write);
      }, debounceMs);
    },
    [casinoInstanceId, sourceId, domain, debounceMs, send],
  );

  const publish = React.useCallback(
    async (arrangement: PageArrangement): Promise<TenantSourceResult<PageSectionsState>> => {
      if (!casinoInstanceId || !sourceId || !creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
      // The waiting draft is held back, not dropped: the publish stores what it sends as the draft too, and if
      // it fails, the edit must still be stored.
      clearTimer();
      const seq = draftSeqRef.current;
      // After a draft write already on its way, as a read waits: landing after the publish, it would leave an
      // older draft stored over the page just published.
      await writingRef.current;
      const result = await publishPageArrangement(casinoInstanceId, sourceId, arrangement, domain);
      if (!result.ok) {
        flushDraft();
        return result;
      }
      if (keyRef.current !== key) return result;
      // An edit made while the publish was out is newer than what it stored: it stays, and is stored after it.
      const newer = draftSeqRef.current !== seq;
      setHeld((prev) => ({
        platform: result.value.platform,
        published: result.value.published,
        draft: newer ? prev.draft : result.value.draft,
      }));
      setStatus('ready');
      clearTimer();
      pendingRef.current = null;
      if (newer && lastDraftRef.current)
        send({ casinoInstanceId, sourceId, domain, arrangement: lastDraftRef.current });
      return result;
    },
    [casinoInstanceId, sourceId, domain, key, flushDraft, send],
  );

  const reload = React.useCallback((): void => {
    // Without a page there is nothing to drop: `dropPageSections` with no page drops every page of the brand.
    if (casinoInstanceId && sourceId) dropPageSections(casinoInstanceId, sourceId);
    setReloadTick((n) => n + 1);
  }, [casinoInstanceId, sourceId]);

  // Syncs with the service for the current (page, brand): read its state, and on the way out send whatever
  // draft is still waiting, under the brand it was made on.
  React.useEffect(() => {
    // A reload of the SAME page keeps what it holds until the answer arrives, so the lists do not jump
    // back to the platform's order for the length of a round trip. A different page starts from nothing.
    const switched = keyRef.current !== key;
    keyRef.current = key;
    if (switched) setHeld(NOTHING_HELD);
    if (!enabled || !sourceId || !casinoInstanceId) {
      setHeld(NOTHING_HELD);
      setStatus('idle');
      return undefined;
    }
    if (switched) setStatus('loading');
    const controller = new AbortController();
    const seq = draftSeqRef.current;
    // After the write the previous run's cleanup just sent, if any (see `writingRef`). The shared read carries
    // no signal: the abort below only tells this run that its answer is no longer wanted.
    void writingRef.current
      .then(() => getPageSectionsShared(casinoInstanceId, sourceId, domain))
      .then((result) => {
        if (controller.signal.aborted || keyRef.current !== key) return;
        if (result.ok) {
          // A draft edited since the read went out is newer than the one it brings back (see `draftSeqRef`).
          const edited = draftSeqRef.current !== seq;
          setHeld((prev) => ({
            platform: result.value.platform,
            published: result.value.published,
            draft: edited ? prev.draft : result.value.draft,
          }));
          setStatus('ready');
        } else {
          // A failed read of the page it already holds keeps that page: the owner can go on arranging it. Only a
          // page never read (a switch sets `loading` first, so a `ready` here is this page's) has nothing to show.
          setStatus((prev) => (prev === 'ready' ? prev : result.reason === 'unavailable' ? 'unavailable' : 'failed'));
        }
      });
    return () => {
      flushDraft();
      controller.abort();
    };
  }, [enabled, key, sourceId, casinoInstanceId, domain, reloadTick, flushDraft]);

  return { status, ...held, setDraft, publish, reload };
};

export default usePageArrangement;
